/**
 * ADSREnvelope - Native Web Audio API ADSR Envelope Implementation
 * 
 * Implements Attack-Decay-Sustain-Release envelope using native GainNode automation.
 * Uses AudioParam scheduling for precise, glitch-free envelope control.
 */

export interface ADSRSettings {
  attack: number;   // 0.001 - 5.0 seconds
  decay: number;    // 0.001 - 5.0 seconds
  sustain: number;  // 0.0 - 1.0 level
  release: number;  // 0.001 - 5.0 seconds
}

export class ADSREnvelope {
  private gainNode: GainNode;
  private audioContext: AudioContext;

  constructor(audioContext: AudioContext, destination: AudioNode) {
    this.audioContext = audioContext;
    this.gainNode = audioContext.createGain();
    
    // Initialize gain to 0 (silent)
    this.gainNode.gain.value = 0;
    
    // Connect to destination
    this.gainNode.connect(destination);
  }

  /**
   * Trigger the ADSR envelope at a specific time
   * 
   * @param time - AudioContext time to start the envelope
   * @param duration - Note duration in seconds (for sustain phase)
   * @param settings - ADSR parameters
   */
  trigger(time: number, duration: number, settings: ADSRSettings): void {
    const { attack, decay, sustain, release } = settings;
    const gain = this.gainNode.gain;

    // Cancel any previously scheduled automation
    gain.cancelScheduledValues(time);

    // Start from 0
    gain.setValueAtTime(0, time);

    // Attack phase: linear ramp from 0 to 1
    const attackEndTime = time + attack;
    gain.linearRampToValueAtTime(1, attackEndTime);

    // Decay phase: exponential approach toward sustain level
    // setTargetAtTime uses a time constant (τ) — value reaches ~95% of target in 3τ
    // We use decay/3 so the decay phase is roughly complete by decayEndTime
    const decayTarget = Math.max(sustain, 0);
    const decayTimeConstant = Math.max(0.001, decay / 3);
    gain.setTargetAtTime(decayTarget, attackEndTime, decayTimeConstant);

    // Sustain phase: hold at sustain level
    const sustainEndTime = attackEndTime + decay + Math.max(0, duration - attack - decay);
    gain.setValueAtTime(decayTarget, sustainEndTime);

    // Release phase: exponential approach toward 0
    const releaseTimeConstant = Math.max(0.001, release / 3);
    gain.setTargetAtTime(0, sustainEndTime, releaseTimeConstant);

    // Hard silence after release to ensure complete cutoff
    const releaseEndTime = sustainEndTime + release;
    gain.setValueAtTime(0, releaseEndTime);
  }

  /**
   * Get the envelope's gain node for audio graph connections
   * 
   * @returns The GainNode used for envelope control
   */
  getGainNode(): GainNode {
    return this.gainNode;
  }

  /**
   * Dispose of the envelope and clean up resources
   * Disconnects the gain node and clears scheduled automation
   */
  dispose(): void {
    try {
      // Cancel any scheduled automation
      this.gainNode.gain.cancelScheduledValues(this.audioContext.currentTime);
      
      // Disconnect the gain node
      this.gainNode.disconnect();
    } catch (error) {
      // Ignore errors from already-disconnected nodes
      console.warn('Error disposing ADSREnvelope:', error);
    }
  }
}
